import React, { useState, useEffect } from 'react'
import { Button, Select, formatSelectOptions } from '../../../components'
import { useMessage } from '../../../hooks'
import { generationService } from '../services'
import '../index.css'

const Generate = () => {
  const { showMessage } = useMessage()

  const [users, setUsers] = useState([])
  const [excludedUserIds, setExcludedUserIds] = useState([])
  const [forcedMegaBuddyUserId, setForcedMegaBuddyUserId] = useState(null)
  // Optional, purely informational — lets everyone see when the round is
  // meant to wrap up, and later feeds a "total stats for this round" view.
  // Kept as a plain yyyy-mm-dd string (matches <input type="date">) rather
  // than a Date, since it's a calendar date, not a precise moment.
  const [roundEndDate, setRoundEndDate] = useState('')
  const [latestRound, setLatestRound] = useState(null)
  // The round that was already active in *history* when this page loaded —
  // captured once on mount and never touched again, regardless of how many
  // times the round gets regenerated this session. This is deliberately
  // NOT "whatever the previous regenerate attempt was" — comparing against
  // a discarded attempt isn't useful; comparing the new round against the
  // last real round (e.g. last week's) is. Reset only on unmount/refresh.
  const [historyBaseline, setHistoryBaseline] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  // Whether latestRound was created by a generate click *this page visit*
  // (as opposed to a pre-existing round loaded from history on mount) —
  // only a session-owned round is safe to replace in place on the next
  // click, so someone can reroll until they're happy without cluttering
  // History with every attempt. A round that already existed before this
  // visit is left alone; the first click on it just adds a new one, which
  // then becomes session-owned and rerollable from then on.
  const [sessionOwnsCurrent, setSessionOwnsCurrent] = useState(false)

  const loadLatest = (signal) => generationService.getHistory({ deleted: false, page: 1, pageSize: 1 }, signal)
    .then(res => {
      const round = res.generations[0] || null
      setLatestRound(round)
      setHistoryBaseline(round)
    })

  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([
      generationService.getUsers(ctrl.signal).then(res => setUsers(res.users || [])),
      loadLatest(ctrl.signal),
    ])
      .then(() => setLoading(false))
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda data', 'error'); setLoading(false) } })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const userOptions = formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name' })

  // Bubbles only stay legible up to a modest headcount — beyond that they'd
  // just wrap into an unreadable wall of pills, so fall back to the plain
  // dropdown instead.
  const BUBBLE_LIMIT = 15
  const showBubbles = users.length <= BUBBLE_LIMIT

  // Same floor as the API's own check — pairing needs at least 2 people.
  // With fewer than that, excluding/forcing a mega buddy is meaningless and
  // generating can't succeed, so the whole form goes inert.
  const MIN_PARTICIPANTS = 2
  const participantCount = users.length - excludedUserIds.length
  const canManageRound = users.length >= MIN_PARTICIPANTS
  const canGenerate = participantCount >= MIN_PARTICIPANTS

  // A mega buddy only ever exists when the non-excluded headcount is odd
  // *and* at least 3 — 1 is technically odd too, but there's no one left to
  // pair a lone remaining person with, mega buddy or otherwise.
  const megaBuddyPossible = participantCount % 2 === 1 && participantCount >= 3

  // Clears a forced pick that's become invalid — e.g. the person just got
  // excluded, or excluding/un-excluding someone else flipped the headcount
  // back to even — rather than leaving a greyed-out bubble stuck "active".
  useEffect(() => {
    if (forcedMegaBuddyUserId && (!megaBuddyPossible || excludedUserIds.includes(forcedMegaBuddyUserId))) {
      setForcedMegaBuddyUserId(null)
    }
  }, [forcedMegaBuddyUserId, megaBuddyPossible, excludedUserIds])

  const toggleExcluded = (userId) => {
    setExcludedUserIds(prev => prev.includes(userId)
      ? prev.filter(id => id !== userId)
      : [...prev, userId])
  }

  const toggleForcedMegaBuddy = (userId) => {
    setForcedMegaBuddyUserId(prev => prev === userId ? null : userId)
  }

  const handleGenerate = () => {
    setGenerating(true)
    const replaceGenerationId = sessionOwnsCurrent && latestRound ? latestRound.generation_id : null
    generationService.generate({ excludedUserIds, forcedMegaBuddyUserId, replaceGenerationId, roundEndTimestamp: roundEndDate || null })
      .then(res => {
        // historyBaseline is deliberately left untouched here — see its
        // declaration above.
        setLatestRound(res.generation)
        setSessionOwnsCurrent(true)
        showMessage(historyBaseline ? 'Ny runda genererad — jämför med föregående runda nedan' : 'Ny runda genererad!')
      })
      .catch(err => showMessage(err.message || 'Kunde inte generera runda', 'error'))
      .finally(() => setGenerating(false))
  }

  const renderRoundCard = (round, title, isNew) => (
    <div className="bud-result-card">
      <div className="bud-result-header">
        <h2>{title}</h2>
        {isNew && <span className="pill bud-pill-new">Ny</span>}
        <span className="bud-result-date">
          Startade {new Date(round.generation_timestamp).toLocaleString('sv-SE')}
          {round.round_end_timestamp && <> — slutar {new Date(round.round_end_timestamp).toLocaleDateString('sv-SE')}</>}
        </span>
      </div>

      <div className="bud-pair-list">
        {round.pairs.map((pair, i) => (
          <div className="bud-pair-row" key={i}>
            <span>{pair.display_name_a}</span>
            <span className="bud-pair-connector">↔</span>
            <span>{pair.display_name_b}</span>
            {round.mega_buddy_user_id && (pair.user_id_a === round.mega_buddy_user_id || pair.user_id_b === round.mega_buddy_user_id) &&
              <span className="pill bud-pill-mega">Mega-buddy</span>
            }
          </div>
        ))}
      </div>

      {round.excluded.length > 0 &&
        <div className="bud-excluded-list">
          <span className="bud-excluded-label">Undantagna:</span> {round.excluded.map(e => e.display_name).join(', ')}
        </div>
      }
    </div>
  )

  if (loading) return <div className="bud-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  return (
    <div className="bud-page">
      <div className="bud-page-header">
        <h1>Generera runda</h1>
      </div>

      <div className="bud-form-card">
        {!canManageRound &&
          <p className="bud-form-notice">Det behövs minst {MIN_PARTICIPANTS} personer för att kunna hantera en runda.</p>
        }
        {canManageRound && !canGenerate &&
          <p className="bud-form-notice">Minst {MIN_PARTICIPANTS} icke-undantagna personer krävs för att generera en runda.</p>
        }

        <div className="form-block vertical">
          <label>Undanta<span className="bud-optional">(valfritt)</span></label>
          {showBubbles
            ? (
              <div className="bud-bubble-toggles bud-bubble-toggles-exclude">
                {userOptions.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    className={"bud-bubble-toggle" + (excludedUserIds.includes(o.value) ? " active" : "")}
                    disabled={!canManageRound}
                    onClick={() => toggleExcluded(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )
            : (
              <Select
                name="excludedUserIds"
                value={excludedUserIds.map(id => userOptions.find(o => o.value === id)).filter(Boolean)}
                options={userOptions}
                onChange={({ value }) => toggleExcluded(value)}
                isMulti
                isClearable
                showMultiLabel
                disabled={!canManageRound}
                placeholder="Välj personer som ska hoppa över denna runda..."
              />
            )
          }
        </div>

        <div className="form-block vertical">
          <label>Tvinga mega-buddy<span className="bud-optional">(valfritt, gäller endast om antalet deltagare blir udda)</span></label>
          {showBubbles
            ? (
              <div className="bud-bubble-toggles bud-bubble-toggles-mega">
                {userOptions.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    className={"bud-bubble-toggle" + (forcedMegaBuddyUserId === o.value ? " active" : "")}
                    disabled={!megaBuddyPossible || excludedUserIds.includes(o.value)}
                    onClick={() => toggleForcedMegaBuddy(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )
            : (
              <Select
                name="forcedMegaBuddyUserId"
                value={userOptions.find(o => o.value === forcedMegaBuddyUserId) || null}
                options={userOptions.filter(o => !excludedUserIds.includes(o.value))}
                onChange={({ value }) => setForcedMegaBuddyUserId(value)}
                isClearable
                disabled={!megaBuddyPossible}
                placeholder="Låt appen välja rättvist..."
              />
            )
          }
        </div>

        <div className="form-block vertical">
          <label>Slutdatum<span className="bud-optional">(valfritt)</span></label>
          <input
            type="date"
            className="bud-plain-input"
            value={roundEndDate}
            onChange={e => setRoundEndDate(e.target.value)}
            disabled={!canGenerate}
          />
        </div>

        <div className="bud-form-actions">
          <Button value="Generera runda" className="button main-button" onClick={handleGenerate} loading={generating} disabled={!canGenerate} />
        </div>
      </div>

      {latestRound &&
        <div className={"bud-compare-rounds" + ((sessionOwnsCurrent && historyBaseline) ? " bud-compare-rounds-two" : "")}>
          {renderRoundCard(latestRound, sessionOwnsCurrent ? "Ny runda" : "Senaste rundan", sessionOwnsCurrent)}
          {sessionOwnsCurrent && historyBaseline && renderRoundCard(historyBaseline, "Föregående runda", false)}
        </div>
      }
    </div>
  )
}

export default Generate
